import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import TaskView from "../common/TaskView";
import { AcousticTask, AcousticTaskDefinition, SubTask, SubTaskDefinition } from "../common/models";
import { randomUUID } from "../../../utils";
import englishHsData from './HarvardSentences.json';
import spanishHsData from './HarvardSentences-es-419.json';
import { Typography } from "@mui/material";
import { i18nLanguageToLabel } from "../../../utils";
import { useStorage } from "../../../../services/StorageProvider";
import { useApiService } from "../../../../services/ApiServiceProvider";

type HarvardSentence = {
  name: string;
  sentences: string[];
};

const TUTORIAL_URLS: Record<string, string> = {
  'en': 'assets/videos/tutorials/Harvard_Sentences.mp4',
  'es-419': 'assets/videos/tutorials/Harvard_Sentences-es-419.mp4'
}

const Definition: AcousticTaskDefinition = {
  name: "Harvard Sentences",
  title: "Harvard Sentences",
  description: "Please read the following sentences out loud in your typical voice.",
  cohort: 'generic',
  type: 'acoustic-task',
  estimatedCompletionTime: '1:30',
  subTaskDefinitions: [],
}

export const HarvardSentencesDefinition = Definition;

const createSubTaskDefinitions = ({ name, sentences }: { name: string, sentences: string[] }): SubTaskDefinition[] => {
  return sentences.map((sentence, index) => ({
    name: `Harvard Sentences-${name}-${index + 1}`,
    title: "",
    instructions: <Typography variant="h5" component='div'>{sentence}</Typography>,
    profileName: 'speech'
  }));
};

const pickHarvardSentenceSets = (hsData: HarvardSentence[]): [HarvardSentence, HarvardSentence] => {
  const firstIndex = Math.floor(Math.random() * hsData.length);
  const firstSet = hsData[firstIndex];
  const filteredHsData = hsData.filter((_, index) => index !== firstIndex);
  const secondSet = filteredHsData[Math.floor(Math.random() * filteredHsData.length)];
  console.log(firstSet, secondSet);
  return [firstSet, secondSet];
};

export const HarvardSentences =  ({onCompletion}: {onCompletion: () => void}) => {
  const { i18n } = useTranslation();
  const apiService = useApiService();
  const { storage } = useStorage();
  const session: any = storage.session;    

  // Map is defined once for performance
  const HARVARD_SENTENCES_MAP: { [key: string]: { name: string; sentences: string[]; }[] } = {
    en: englishHsData,
    'es-419': spanishHsData
  };

  const getHarvardSentencesForLanguage = (language: string) => {
    return HARVARD_SENTENCES_MAP[language] || englishHsData;
  };

  const [selectedHarvardSets] = useState<[HarvardSentence, HarvardSentence]>(() => {
    const hsData = getHarvardSentencesForLanguage(i18n.language);
    return pickHarvardSentenceSets(hsData);
  });

  const getSubTasks = (): SubTask[] => {
    const task1 = createSubTaskDefinitions(selectedHarvardSets[0]);
    const task2 = createSubTaskDefinitions(selectedHarvardSets[1]);

    const selectedSubTasks = [...task1, ...task2];
    return selectedSubTasks.map((definition) => ({ ...definition, id: randomUUID() }));
  };

  const [task, _setTask] = useState<AcousticTask>(() => ({
    ...Definition,
    id: randomUUID(),
    startedAt: new Date().toISOString(),
    completedAt: null,
    status: 'pending',
    subTasks: getSubTasks(),
    tutorialUrl: TUTORIAL_URLS[i18n.language] || TUTORIAL_URLS['en']
  }));

  const handleOnCompletion = () => {
    console.log("Audio task completed", task.title);
    onCompletion();
  }

  const handleOnCreateQuestionnaire = async () => {
    const startAt = task.startedAt || new Date().toISOString();
    const completedAt = task.completedAt || new Date().toISOString();
    const payload = {
      sessionId: session.id,
      subjectId: session.subjectId,
      startedAt: startAt,
      completedAt: completedAt,
      acousticTaskId: task.id,
      items: selectedHarvardSets,
      language: i18nLanguageToLabel(i18n.language)
    };

    await apiService.createQuestionnaire('/api/harvardSentencesQuestionnaires', payload);
  };

  return (
    <div>
      <TaskView
        task={task}
        onCompletion={handleOnCompletion}
        createQuestionnaire={handleOnCreateQuestionnaire}
        />
    </div>
  );
}

